import {DOCUMENT} from '@angular/common';
import {computed, inject, Injectable, signal} from '@angular/core';
import {
	MonthDay,
	SEASONAL_THEME_OVERRIDE,
	SEASONAL_THEMES,
	SeasonalThemeConfig,
	SeasonalThemeId,
} from '@/app/config/seasonal-themes.config';

const toOrdinal = ({month, day}: MonthDay): number => month * 100 + day;

export function isInPeriod(date: Date, {start, end}: Pick<SeasonalThemeConfig, 'start' | 'end'>): boolean {
	const now = toOrdinal({month: date.getMonth() + 1, day: date.getDate()});
	const from = toOrdinal(start);
	const to = toOrdinal(end);
	return from <= to ? now >= from && now <= to : now >= from || now <= to;
}

export function resolveSeasonalTheme(date: Date): SeasonalThemeId | null {
	return SEASONAL_THEMES.find(theme => theme.enabled && isInPeriod(date, theme))?.id ?? null;
}

@Injectable({providedIn: 'root'})
export class SeasonalThemeService {
	private readonly document = inject(DOCUMENT);

	private readonly _theme = signal<SeasonalThemeId | null>(null);
	readonly theme = this._theme.asReadonly();
	readonly isActive = computed(() => this._theme() !== null);

	/** À appeler une fois au démarrage : calcule le thème et l'expose via `<html data-season>`. */
	init(): void {
		const theme = this.resolve();
		this._theme.set(theme);

		const root = this.document.documentElement;
		if (theme) {
			root.setAttribute('data-season', theme);
		} else {
			root.removeAttribute('data-season');
		}
	}

	private resolve(): SeasonalThemeId | null {
		const queryOverride = this.readQueryOverride();
		const forced = queryOverride ?? SEASONAL_THEME_OVERRIDE;
		if (forced === 'none') return null;
		if (forced) return forced;
		return resolveSeasonalTheme(new Date());
	}

	private readQueryOverride(): SeasonalThemeId | 'none' | null {
		const search = this.document.defaultView?.location?.search;
		if (!search) return null;
		const value = new URLSearchParams(search).get('season');
		return value === 'halloween' || value === 'christmas' || value === 'none' ? value : null;
	}
}
